import { z } from 'zod';
import { jsonValueSchema } from '../flow/json-pointer';

// The ratified BAM (Brief Addiction Monitor) content contract — the single
// Zod-owned source of truth for the questionnaire definition and a member's
// answers, shared verbatim by the api (validating what it serves/stores) and
// the mobile client (validating what it renders). The client stays
// condition-agnostic (ADR-015): it renders whatever prompts + options this
// contract carries and never hard-codes BAM specifics.

// An answered value. `single_select` questions store the chosen option `key`
// (a string); a `number` question would store an integer; `null` clears an
// answer. The `number` arm is carried for forward-compat but unused this spike
// (no numeric renderer yet).
export const bamAnswerValueSchema = z.union([z.string(), z.number().int(), z.null()]);
export type BamAnswerValue = z.infer<typeof bamAnswerValueSchema>;

// One ordered answer option. `value` is a scoring weight carried for later
// scoring work but unused now; it may be null when a scale is not yet scored.
export const bamAnswerOptionSchema = z
  .object({
    key: z.string().min(1),
    label: z.string().min(1),
    value: z.number().int().nullable(),
  })
  .strict();
export type BamAnswerOption = z.infer<typeof bamAnswerOptionSchema>;

// Server-authored wording variants for a question, chosen by one member context
// value (for example a drink threshold). The client picks the variant whose key
// equals the value at `/context/<contextKey>` and falls back to `prompt` for any
// other value or when context is absent, so it never branches on what the value
// means (ADR-015).
export const bamPromptVariantsSchema = z
  .object({
    contextKey: z.string().min(1),
    byValue: z.record(z.string().min(1), z.string().min(1)),
  })
  .strict();
export type BamPromptVariants = z.infer<typeof bamPromptVariantsSchema>;

// A `/`-separated path under the `/context` state root whose string value, when
// present, is shown as a confirm-only hint under the question.
const contextPathSchema = z
  .string()
  .regex(/^[a-z0-9_]+(\/[A-Za-z0-9_]+)*$/, 'a context path is slash-separated segments');

const bamQuestionBaseSchema = z.object({
  name: z.string().min(1),
  prompt: z.string().min(1),
  promptVariants: bamPromptVariantsSchema.optional(),
  hintContextPath: contextPathSchema.optional(),
});

// A skip rule: the declaring question is not asked while an earlier
// single-select question (`question`) holds the option `equals`. When the rule
// names `impliedOptionKey`, a reader treats the skipped item as that option of
// the declaring question (the code the skip stands for); without it a skipped
// item carries no code. The client never interprets the rule, it only turns it
// into a navigation condition (ADR-015).
export const bamSkipRuleSchema = z
  .object({
    question: z.string().min(1),
    equals: z.string().min(1),
    impliedOptionKey: z.string().min(1).optional(),
  })
  .strict();
export type BamSkipRule = z.infer<typeof bamSkipRuleSchema>;

export const bamSingleSelectQuestionSchema = bamQuestionBaseSchema
  .extend({
    type: z.literal('single_select'),
    options: z.array(bamAnswerOptionSchema).min(2),
    skippedWhen: bamSkipRuleSchema.optional(),
  })
  .strict();
export type BamSingleSelectQuestion = z.infer<typeof bamSingleSelectQuestionSchema>;

// Carried in the contract for forward-compat (raw day-count items); no client
// renderer exists yet, so the mapper surfaces an explicit error for this type
// rather than silently skipping it.
export const bamNumberQuestionSchema = bamQuestionBaseSchema
  .extend({
    type: z.literal('number'),
    min: z.number().int().optional(),
    max: z.number().int().optional(),
    unit: z.string().min(1).optional(),
  })
  .strict();
export type BamNumberQuestion = z.infer<typeof bamNumberQuestionSchema>;

export const bamQuestionSchema = z.discriminatedUnion('type', [
  bamSingleSelectQuestionSchema,
  bamNumberQuestionSchema,
]);
export type BamQuestion = z.infer<typeof bamQuestionSchema>;

export const bamQuestionTypeSchema = z.enum(['single_select', 'number']);
export type BamQuestionType = z.infer<typeof bamQuestionTypeSchema>;

// The versioned questionnaire content: an ordered item set. Member-facing copy
// (title, home-card copy) lives outside `definition` — the definition is
// `{questions}` only (card copy is server-authored in the home view model).
//
// `contextProviders` names the member-context providers the server resolves for
// an attempt on this version. Whether each name is a registered provider is
// checked on the server, which owns the provider registry. Here, every context
// a question reads (its wording key and its hint path's first segment) must be
// one the definition declares, so a misspelt reference fails at authoring time
// instead of silently rendering the base wording.
export const bamDefinitionSchema = z
  .object({
    questions: z.array(bamQuestionSchema).min(1),
    contextProviders: z.array(z.string().min(1)).optional(),
  })
  .strict()
  .superRefine((definition, ctx) => {
    const declared = new Set(definition.contextProviders ?? []);
    definition.questions.forEach((question, index) => {
      const wordingKey = question.promptVariants?.contextKey;
      if (wordingKey !== undefined && !declared.has(wordingKey)) {
        ctx.addIssue({
          code: 'custom',
          path: ['questions', index, 'promptVariants', 'contextKey'],
          message: `context "${wordingKey}" is not declared in contextProviders`,
        });
      }
      const hintRoot = question.hintContextPath?.split('/')[0];
      if (hintRoot !== undefined && !declared.has(hintRoot)) {
        ctx.addIssue({
          code: 'custom',
          path: ['questions', index, 'hintContextPath'],
          message: `context "${hintRoot}" is not declared in contextProviders`,
        });
      }
    });
    validateSkipRules(definition.questions, ctx);
  });

// The rule key two questions share when one gate skips both.
function skipRuleKey(rule: BamSkipRule): string {
  return `${rule.question}\u0000${rule.equals}`;
}

function skipRuleOf(question: BamQuestion): BamSkipRule | undefined {
  return question.type === 'single_select' ? question.skippedWhen : undefined;
}

// Every skip must be a single forward jump the mapper can author as one
// navigation rule: the gate precedes its gated questions and is never gated
// itself, questions sharing a gate sit together, and the question after such
// a run (when there is one) is not gated, so two runs never touch.
function validateSkipRules(questions: BamQuestion[], ctx: z.RefinementCtx): void {
  const issue = (index: number, message: string) =>
    ctx.addIssue({ code: 'custom', path: ['questions', index, 'skippedWhen'], message });

  questions.forEach((question, index) => {
    const rule = skipRuleOf(question);
    if (!rule) return;
    const gateIndex = questions.findIndex((q) => q.name === rule.question);
    const gate = gateIndex === -1 ? undefined : questions[gateIndex];
    if (!gate) {
      issue(index, `gating question "${rule.question}" is not in the definition`);
    } else if (gateIndex >= index) {
      issue(index, `gating question "${rule.question}" must come before "${question.name}"`);
    } else if (gate.type !== 'single_select') {
      issue(index, `gating question "${rule.question}" must be single_select`);
    } else if (gate.skippedWhen) {
      issue(index, `gating question "${rule.question}" is itself gated`);
    } else if (!gate.options.some((o) => o.key === rule.equals)) {
      issue(index, `"${rule.equals}" is not an option key of "${rule.question}"`);
    }
    if (
      rule.impliedOptionKey !== undefined &&
      question.type === 'single_select' &&
      !question.options.some((o) => o.key === rule.impliedOptionKey)
    ) {
      issue(index, `"${rule.impliedOptionKey}" is not an option key of "${question.name}"`);
    }
  });

  // Runs: a maximal stretch of consecutive questions sharing one rule key.
  const seenKeys = new Set<string>();
  let runKey: string | undefined;
  questions.forEach((question, index) => {
    const rule = skipRuleOf(question);
    const key = rule ? skipRuleKey(rule) : undefined;
    if (key !== undefined && key !== runKey) {
      if (seenKeys.has(key)) {
        issue(
          index,
          `questions gated by "${rule?.question}" = "${rule?.equals}" must be contiguous`,
        );
      }
      if (runKey !== undefined) {
        issue(index, `"${question.name}" follows a gated run and must not be gated`);
      }
      seenKeys.add(key);
    }
    runKey = key;
  });
}
export type BamDefinition = z.infer<typeof bamDefinitionSchema>;

// The member context resolved for one attempt, keyed by provider name. Stored
// on the attempt when first read (an answer save may later update a wording
// value), so its values are opaque JSON here; each provider's own schema is
// applied where the value is produced.
export const bamContextSnapshotSchema = z.record(z.string().min(1), jsonValueSchema);
export type BamContextSnapshot = z.infer<typeof bamContextSnapshotSchema>;

// A member's answers bag: a partial map from question `name` to answered value.
// Keys are trusted server-produced question names, so a plain string-keyed
// record captures the partial nature of an in-progress attempt.
export const bamResponsesSchema = z.record(z.string(), bamAnswerValueSchema);
export type BamResponses = z.infer<typeof bamResponsesSchema>;

// Governed enum source arrays (ADR-056 Rule 8): the DB `text`+CHECK constraints
// derive their literal set from these, so the constraint and the wire schema
// cannot drift.
export const BAM_QUESTIONNAIRE_STATUS = ['active', 'draft', 'retired'] as const;
export const bamQuestionnaireStatusSchema = z.enum(BAM_QUESTIONNAIRE_STATUS);
export type BamQuestionnaireStatus = z.infer<typeof bamQuestionnaireStatusSchema>;

export const BAM_ASSESSMENT_STATUS = ['in_progress', 'completed'] as const;
export const bamAssessmentStatusSchema = z.enum(BAM_ASSESSMENT_STATUS);
export type BamAssessmentStatus = z.infer<typeof bamAssessmentStatusSchema>;
